import { useCallback, useMemo } from 'react';

import { BridgeSubscriptionMethod, IdAndEventSubscriptionConfig } from '@scaleaq/sitecontrol-lib';
import { useQuery } from '@tanstack/react-query';

import { useSiteContext } from '@/providers';

import { useCurrentSiteConnection } from '../../connection';
import { updateSubscribedToEvent, useBridgeEventSubscription } from '../../registerAndSubscribe';

export const useMultiSensorEventSubscription = (
  idsToSubscribe: string[],
  eventName: string,
  listenerId: string
) => {
  const { currentSite } = useSiteContext();
  const { connectionId } = useCurrentSiteConnection();

  // Include the site id, event name and connection id to force resubscription when the site changes
  // or the connection is re-established.
  const queryKey = useMemo<string[]>(
    () => [`multiple-sensors-${currentSite?.id}-${eventName}`, connectionId, ...idsToSubscribe],
    [currentSite?.id, eventName, connectionId, idsToSubscribe]
  );
  const { subscribeToEvent, isLoading } = useBridgeEventSubscription<IdAndEventSubscriptionConfig>(
    BridgeSubscriptionMethod.SubscribeByIdAndEvent
  );

  const getter = useCallback(() => {
    if (isLoading) {
      return false;
    }
    return Promise.all(
      idsToSubscribe.map(id => {
        const subscriptionArgs: IdAndEventSubscriptionConfig = {
          id: id,
          eventName: eventName,
          listenerId: listenerId,
        };
        return subscribeToEvent(subscriptionArgs);
      })
    )
      .then(() =>
        updateSubscribedToEvent({
          eventNamesOrIds: idsToSubscribe,
          eventNameForIds: eventName,
          currentSiteId: currentSite?.id ?? '',
        })
      )
      .then(() => true)
      .catch(() => false);
  }, [isLoading, idsToSubscribe, eventName, listenerId, subscribeToEvent, currentSite?.id]);

  const query = useQuery<boolean>({
    enabled: !isLoading,
    queryKey: queryKey,
    queryFn: getter,
    refetchOnWindowFocus: false,
  });
  return { ...query, isLoading: query.isLoading || isLoading };
};
